/**
 * Notification Store
 * Manages in-app notifications that appear in the bell dropdown
 */

const STORAGE_KEY = 'streakfree_notifications';
const MAX_NOTIFICATIONS = 50;

// Notification types
export const NotificationType = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error'
};

/**
 * Get all notifications from storage
 */
export const getNotifications = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    console.error('Error reading notifications:', err);
    return [];
  }
};

/**
 * Get unread notification count
 */
export const getUnreadCount = () => {
  const notifications = getNotifications();
  return notifications.filter(n => !n.read).length;
};

/**
 * Add a new notification
 */
export const addNotification = (notification) => {
  try {
    const notifications = getNotifications();
    
    const newNotification = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: notification.title,
      message: notification.message,
      type: notification.type || NotificationType.INFO,
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };
    
    // Add to beginning of array
    notifications.unshift(newNotification);
    
    // Limit to MAX_NOTIFICATIONS
    const trimmed = notifications.slice(0, MAX_NOTIFICATIONS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('notification-added', { detail: newNotification }));
    
    return newNotification;
  } catch (err) {
    console.error('Error adding notification:', err);
    return null;
  }
};

/**
 * Mark notification as read
 */
export const markAsRead = (notificationId) => {
  try {
    const notifications = getNotifications();
    const updated = notifications.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notification-updated'));
  } catch (err) {
    console.error('Error marking notification as read:', err);
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = () => {
  try {
    const notifications = getNotifications();
    const updated = notifications.map(n => ({ ...n, read: true }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('notification-updated'));
  } catch (err) {
    console.error('Error marking all as read:', err);
  }
};

/**
 * Clear a single notification
 */
export const clearNotification = (notificationId) => {
  try {
    const notifications = getNotifications();
    const filtered = notifications.filter(n => n.id !== notificationId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new CustomEvent('notification-updated'));
  } catch (err) {
    console.error('Error clearing notification:', err);
  }
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    window.dispatchEvent(new CustomEvent('notification-updated'));
  } catch (err) {
    console.error('Error clearing notifications:', err);
  }
};

/**
 * Subscribe to notification changes
 */
export const subscribeToNotifications = (callback) => {
  const handleAdded = (e) => callback('added', e.detail);
  const handleUpdated = () => callback('updated', null);
  
  window.addEventListener('notification-added', handleAdded);
  window.addEventListener('notification-updated', handleUpdated);
  
  return () => {
    window.removeEventListener('notification-added', handleAdded);
    window.removeEventListener('notification-updated', handleUpdated);
  };
};
